import {
  Building2, Wrench, Pill, ClipboardList, Timer, Droplets, Wind, Zap, CalendarDays, Monitor, ClipboardPen,
} from 'lucide-react';

/*
 * VA (Michael E. DeBakey VA Medical Center) General OR rotation. Built from the Blackboard
 * VA folders: Full VAMC Orientation (slides, Feb 2020), VA Preop and OR Setup, EBUS guidelines,
 * Anesthesia for ECT, and the Innovian charting guide. See docs/blackboard-va.md.
 * The VA charts in Innovian and keeps the record in CPRS; there is no Epic here.
 */

export const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const WELCOME = BB_DOC('_255083_1');
const GENERAL = BB_DOC('_255081_1');
const INNOVIAN = BB_DOC('_255092_1');

export const vaOr = {
  accent: '#1f4573',
  icon: Building2,
  tagline: 'Main OR: ORC board, Omnicell, Innovian, turnover',
  notesKey: 'notes:va:or',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'setup', title: 'OR Setup', subtitle: 'MSMAID, drugs, where they live, labels', icon: Wrench, action: 'guide:setup', featured: true },
      { id: 'narcotics', title: 'Controlled Substances', subtitle: 'Omnicell rules, wasting, what counts', icon: Pill, action: 'guide:narcotics' },
      { id: 'preop', title: 'Preop & Holding', subtitle: 'The preop call, the patient check, type and screen, lines', icon: ClipboardList, action: 'guide:preop' },
      { id: 'turnover', title: 'Start Times & Turnover', subtitle: 'Deadlines, the three icons, who to call', icon: Timer, action: 'guide:turnover' },
      { id: 'blood', title: 'Blood', subtitle: 'Type and screen, blood forms', icon: Droplets, action: 'guide:blood' },
      { id: 'ebus', title: 'Bronchoscopy & EBUS', subtitle: 'LMA, TIVA, rigid scope with jet ventilation', icon: Wind, action: 'guide:ebus' },
      { id: 'ect', title: 'ECT', subtitle: 'Physiology, drugs, the VA setup', icon: Zap, action: 'guide:ect' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Vocera, ORC, scrubs, call roles, end of day', icon: CalendarDays, action: 'guide:day' },
      { id: 'orc', title: 'ORC Board', subtitle: 'Reading the digital OR board', icon: Monitor, action: 'guide:orc' },
      { id: 'innovian', title: 'Innovian & CPRS', subtitle: 'Preop, intraop, closing and transmitting', icon: ClipboardPen, action: 'guide:innovian' },
    ] },
  ],

  contacts: {
    storageKey: 'va-or-favorites',
    groups: [
      { id: 'vocera', title: 'Vocera', open: true, items: [
        { label: 'Vocera, from outside the hospital', phone: '7137701570', note: 'Say the name, or spell it. "Anesthesia technician" for the techs. In-house dial 21570' },
      ] },
      { id: 'board', title: 'Board runners and chief', open: true, items: [
        { label: 'Dr Prasad Atluri, Chief of Anesthesia', phone: '7134088604', note: 'Vocera first. Cell after hours' },
        { label: 'Dr Thuy Ngo, board runner', phone: '2818140230', note: 'Vocera first. Cell after hours' },
        { label: 'Dr Edmund Ho, board runner', phone: '8327546128', note: 'Vocera first. Cell after hours' },
      ] },
      { id: 'admin', title: 'Coordinators', items: [
        { label: 'LaToya Medlock', phone: '7137911411', note: 'Extension 26410. Badges, access codes, blood forms' },
        { label: 'Leann Lyons', phone: '7137947661' },
      ] },
      { id: 'support', title: 'Support', items: [
        { label: 'Anesthesia tech workroom', phone: '7137911414', note: 'Extension 26339. Vocera "Anesthesia technician" is faster' },
        { label: 'Blood bank', phone: '7137911414', note: 'Extension 27255. Whether a type and screen is needed' },
      ] },
    ],
  },

  locations: {
    groups: [
      { id: 'or', title: 'Operating rooms', items: [
        { name: 'Main OR holding', floor: 'Main OR', note: 'PACU is holding for first cases, then the front preop bay by the trainee coffee lounge' },
        { name: 'Blocks', floor: 'PACU', where: 'Spots 8 and 9', note: 'Regional team, all day' },
        { name: 'Main OR Omnicell', floor: 'Main OR', note: 'Pull narcotics here, not the PACU Omnicell. Waste at the PACU Omnicell' },
        { name: 'Med room fridge', floor: 'Main OR', note: 'Phenylephrine, norepinephrine, nitroglycerin' },
        { name: 'Anesthesia workroom', floor: 'Main OR', note: 'Suction setups, circuits, machines' },
        { name: 'Computer room', floor: 'PACU', note: 'Preops and printing. Printer HOU-PT143094; PACU printer 169538' },
      ] },
      { id: 'elsewhere', title: 'Elsewhere', items: [
        { name: 'SACC', floor: '5H', note: 'Surgical Ambulatory Care Center. Outpatients and first block cases start here' },
        { name: 'Facilities Management', floor: '4th floor', note: 'Badge activation for OR areas' },
      ] },
    ],
  },

  documents: {
    storageKey: 'va-or-pinned-docs',
    groups: [
      { id: 'orientation', title: 'Orientation', items: [
        { title: 'Full VAMC orientation', subtitle: '72 slides, February 2020. Blackboard', url: WELCOME },
        { title: 'Preop and OR setup', subtitle: 'General and cardiac. Blackboard', url: GENERAL },
        { title: 'EBUS and ECT guides', subtitle: 'Blackboard', url: GENERAL },
        { title: 'Innovian charting', subtitle: 'Blackboard', url: INNOVIAN },
      ] },
      { id: 'tools', title: 'Tools', items: [
        { title: 'QGenda', subtitle: 'Schedule and staff phone numbers', url: 'https://app.qgenda.com' },
      ] },
    ],
  },

  guides: {
    setup: {
      title: 'OR Setup',
      sections: [
        { title: 'Before you set up', blocks: [{ type: 'bullets', items: [
          'Check ORC first. Cases get switched',
          'VA scrubs only, never outside the building. Caps, masks and shoe covers stay in the OR area',
          'MSMAID: machine, suction, monitors, airway, IV, drugs',
          'Special equipment: Vocera "Anesthesia technician"',
        ] }] },
        { title: 'Drugs for a general case', blocks: [{ type: 'rows', rows: [
          { label: 'Blue cart', value: 'Lidocaine, propofol, rocuronium, esmolol', note: 'Paralytics from the cart, not the fridge' },
          { label: 'Med room fridge', value: 'Phenylephrine, norepinephrine, nitroglycerin', note: 'One of each. Call for more' },
          { label: 'Blue cart vial', value: 'Ephedrine, dilute it yourself' },
          { label: 'Omnicell', value: 'Narcotics, IV acetaminophen, dexmedetomidine, extra propofol', note: 'First patient only' },
          { label: 'Omnicell, ask first', value: 'Sugammadex', note: 'Expensive here. Attending agrees before you pull it' },
        ] }] },
        { title: 'Labels', blocks: [{ type: 'bullets', items: [
          'Expiry date: tomorrow by convention. Propofol expires in 12 hours',
          'Your initials',
        ] }] },
        { title: 'Cardiac case', blocks: [{ type: 'rows', rows: [
          { label: 'Carrier', value: '500 mL NS with Amicar 10 g or TXA 1–2 g', note: 'Attending preference' },
          { label: 'Vasopressin', value: '50 U in 50 mL, 1 U/mL', note: 'Premixed, fridge. First-line pressor, on the pump' },
          { label: 'Epinephrine', value: '2.5 mg in 250 mL, 10 mcg/mL', note: 'Premixed, on the pump' },
          { label: 'Norepinephrine', value: '4 mg in 250 mL, 16 mcg/mL', note: 'Premixed, on the pump' },
          { label: 'Insulin', value: '100 U in 100 mL', note: 'Premixed, no tubing' },
          { label: 'On the cart, unopened', value: 'Milrinone, nicardipine drips' },
        ] }, { type: 'rows', rows: [
          { label: 'Epinephrine', value: '10 mcg/mL' },
          { label: 'Norepinephrine', value: '16 mcg/mL' },
          { label: 'Phenylephrine', value: '100 mcg/mL' },
          { label: 'Ephedrine', value: '5 mg/mL' },
          { label: 'Nitroglycerin', value: '100 mcg/mL' },
          { label: 'Esmolol', value: 'One 10 mL syringe' },
          { label: 'Midazolam', value: '6 mg' },
          { label: 'Fentanyl', value: '1000 mcg' },
          { label: 'Heparin', value: '3 mg/kg in a 30 mL syringe' },
          { label: 'Amicar or TXA', value: '10 g in 60 mL, or 1–2 g in 20 mL' },
          { label: 'Etomidate', value: '' },
          { label: 'Cisatracurium or rocuronium', value: '' },
        ] }, { type: 'bullets', items: [
          'Antibiotics: vancomycin 1 g in 250 mL NS or the premixed bag; cefepime 1 g, or gentamicin or ciprofloxacin for penicillin allergy per the surgeon',
          'NIRS for ascending aorta, arch or carotid surgery. Belmont for thoracoabdominal aneurysm or redo sternotomy',
        ] }] },
      ],
      sources: [{ title: 'VA preop and OR setup', href: GENERAL, detail: 'Blackboard, sign-in required' }, { title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    narcotics: {
      title: 'Controlled Substances',
      sections: [
        { title: 'What counts here', blocks: [{ type: 'bullets', items: [
          'Fentanyl, remifentanil, sufentanil, morphine, hydromorphone, midazolam, ketamine, methohexital',
        ] }] },
        { title: 'Pulling', blocks: [{ type: 'bullets', items: [
          'Main OR Omnicell, not the PACU one',
          'Immediately before the case, for one case at a time',
          'Only the amount you agreed with the attending. 4 mg midazolam and 500 mcg fentanyl is not a MAC case',
          'Log off the Omnicell every time. Anything pulled while you are logged in is under your name',
        ] }] },
        { title: 'Carrying', blocks: [{ type: 'bullets', items: [
          { text: 'Never leave narcotics unattended in the blue cart or hanging as a drip', warn: true },
          'Leaving the room: unopened vials, capped syringes and drip bags go with you in a biohazard bag',
          'Relieved mid-case: document the handover in Innovian under Staff, your name, note, sign',
          'Lock the blue cart whenever you leave',
        ] }] },
        { title: 'Wasting', blocks: [{ type: 'bullets', items: [
          'Immediately after the case, before pulling for the next one',
          'Unopened vials go back to the Main OR Omnicell before you waste',
          'Waste at the PACU Omnicell under the correct patient. Totals match the record',
          'With your attending, or any anesthesia person in the PACU',
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    preop: {
      title: 'Preop & Holding',
      sections: [
        { title: 'The day before', blocks: [{ type: 'bullets', items: [
          'Complete every preop for your room. Inpatients are seen in person',
          'You own the preop even when a nurse, CRNA or another trainee wrote it. Read the Innovian preop and CPRS fully',
          'Call the attending before you leave the hospital: Vocera, then the cell number in QGenda. Leave a voicemail with your callback',
          'Flag it on ORC: open the patient box, No Anes PreEval form, change to Anes PreEval Form Done',
        ] }] },
        { title: 'What the attending wants to hear', blocks: [{ type: 'bullets', items: [
          'Procedure. History, exam, labs, imaging',
          'Primary anesthetic: spinal, general, regional, MAC',
          'Premedication and prophylaxis',
          'Induction, tube or LMA, TIVA or volatile, paralysis',
          'Lines and monitors',
          'Deep or awake extubation',
          'Postoperative analgesia: IV, blocks',
          'ICU or PACU',
          'What could go wrong: bleeding, bronchospasm',
        ] }] },
        { title: 'In holding', blocks: [{ type: 'bullets', items: [
          'There by 07:00. Wednesdays 08:00, after Grand Rounds',
          'Wristband: name and last four of the SSN. Allergies. The procedure in the patient’s own words. Airway. NPO',
          'Open questions from the preop call. Beta blocker taken this morning. Sleep apnea and CPAP',
          'Type and screen needed? Ask the attending or the blood bank',
          'Press the black button on the clipboard, or Anesthesia Consult on ORC. The A turns green',
        ] }] },
        { title: 'Lines', blocks: [{ type: 'bullets', items: [
          'IV first. Draw the type and screen (pink top) or VBG from it',
          'Lidocaine for the skin, then safety-lock the needle. Never reuse a lidocaine syringe',
          'Two misses: call the attending',
          'Running out of time: the IV and arterial line can go in the OR. Call the attending if either is slipping',
          'Eye protection for every procedure. Sharps in the bin at once, capped or not',
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }, { title: 'VA preop and OR setup', href: GENERAL, detail: 'Blackboard, sign-in required' }],
    },

    turnover: {
      title: 'Start Times & Turnover',
      sections: [
        { title: 'Deadlines', blocks: [{ type: 'rows', rows: [
          { label: 'First case', value: 'In the room by 07:30', note: 'Wednesdays 08:30' },
          { label: 'Following cases', value: '30 min after Patient Leaves the OR', note: '45 min with a robot setup or an ICU drop-off' },
        ] }, { type: 'note', text: 'Set a 20-minute phone timer when you roll out. It rings with about ten minutes left.' }] },
        { title: 'Icons not turning green', blocks: [{ type: 'steps', items: [
          'The A: press Anesthesia Consult on the clipboard or ORC yourself',
          'The stretcher, 5–10 min before the deadline: ask the holding nurse at the desk what is pending',
          'The door, 5–10 min before the deadline: call the OR nurse at the number above the room on ORC. "This is [name] with anesthesia. Are we ok to roll? We are due back at [time]"',
        ] }] },
        { title: 'Between cases', blocks: [{ type: 'bullets', items: [
          'In the PACU: close the chart, report to the nurse, waste narcotics',
          'Main OR Omnicell for the next patient’s narcotics. Prep the airway and draw up in your room',
          'Holding with your paper preop in hand to see the next patient and place the IV',
          'If the patient is stable and the chart is done, draw up non-narcotic drugs for the next case during the current one',
          { text: 'Any delay: tell the attending and chart it in Innovian under Outcomes', warn: true },
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    blood: {
      title: 'Blood',
      sections: [
        { title: 'Type and screen', blocks: [{ type: 'bullets', items: [
          'Needed if blood might be used and there is no type and screen in the system from the last 72 hours. When unsure, draw it: 3–5 mL in a pink top',
          'Label with the patient sticker plus handwritten date, time, your three initials and a second person’s three initials. Otherwise the blood bank refuses it',
          'Re-verify name and SSN against the labelled tube with the patient',
        ] }] },
        { title: 'Blood forms', blocks: [{ type: 'bullets', items: [
          'After the nurse scans the unit, check it against its form. Two people sign as first and second identifier, with the date',
          'If transfused: start and stop vitals on the form, not "see anesthesia record". Volume from the bag, or 250 mL for a unit of red cells. Complications boxes, signature, date',
          'Left incomplete, the coordinators come find you',
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    ebus: {
      title: 'Bronchoscopy & EBUS',
      sections: [
        { title: 'Before', blocks: [{ type: 'bullets', items: [
          'Flexible, rigid, or EBUS with transbronchial needle aspiration',
          'Heavy cardiopulmonary disease is the norm; some patients are on home oxygen',
          'Mass or lymphadenopathy: think airway obstruction. Read the pulmonology note, the CT and the PFTs',
          'Rigid scope planned: check cervical spine mobility and pain',
        ] }] },
        { title: 'Anesthetic', blocks: [{ type: 'bullets', items: [
          'Standard monitors. Arterial line for comorbidities or a long case',
          'LMA usually; it lets the pulmonologist see the glottis and spares the airway. ETT 8.0 or 9.0 when needed',
          'TIVA with propofol. Fentanyl boluses or remifentanil per the attending',
          'Most are paralysed to stop coughing and movement. Dose and agent with the attending',
        ] }] },
        { title: 'Rigid bronchoscopy', blocks: [{ type: 'bullets', items: [
          'Jet ventilator in the room attached to the scope side port. Oxygen on',
          'Low frequency 10–30 breaths/min is usual; high frequency 120–600 for some',
          'Tidal volume by driving pressure, starting around 20 psi',
          'Watch chest rise, saturation and blood pressure. Pneumothorax is the risk',
          { text: 'Expiratory path must stay open or you get barotrauma', warn: true },
        ] }] },
      ],
      sources: [{ title: 'VA EBUS anesthesia guidelines', href: GENERAL, detail: 'Blackboard, sign-in required' }],
    },

    ect: {
      title: 'ECT',
      segments: [
        { id: 'va', label: 'VA setup', sections: [
          { title: 'The morning', blocks: [{ type: 'bullets', items: [
            'Cases start 08:00–08:30 and then move fast. Have everything ready for every patient before the first',
            'A blue cart with nearly everything arrives in the isolation room in the morning',
            'Prior treatments: the CPRS note "MH ECT Note" has the drugs used and what to change. Print it with the preops',
            'One labelled bag of drawn-up drugs per patient. Extra esmolol and phenylephrine drawn',
            'Most patients arrive without an IV. You or the attending place them between cases. A fluid bag per patient',
          ] }] },
          { title: 'Methohexital', blocks: [{ type: 'bullets', items: [
            'Controlled: pull from the Omnicell, waste after',
            '500 mg vial. Dilute it yourself, or call pharmacy after 07:00 to stock 10 mL sticks of 10 mg/mL in the Omnicell and pull one or two per patient',
          ] }] },
          { title: 'Airway kit', blocks: [{ type: 'bullets', items: [
            'Jackson-Rees circuit: circuit with bag, mask, CO2 connector. Back of the blue cart or the workroom',
            'One circuit per patient with an oral airway, wrapped in a blue towel',
            'LMAs and tubes in the blue cart. Extra suction setups from the workroom',
          ] }] },
        ] },
        { id: 'drugs', label: 'Drugs', sections: [
          { title: 'Induction', blocks: [{ type: 'rows', rows: [
            { label: 'Methohexital', value: '1–1.5 mg/kg', note: 'Gold standard. Lowers the threshold, blunts the sympathetic surge. Slower wake, movements, porphyria' },
            { label: 'Propofol', value: '1–2 mg/kg', note: 'Shorter seizure, better hemodynamics, faster wake. May need more voltage' },
            { label: 'Etomidate', value: '0.1–0.3 mg/kg', note: 'Longer seizure. No hemodynamic blunting. PONV, adrenal suppression' },
            { label: 'Ketamine', value: '1–2 mg/kg', note: 'Worse hemodynamics and delirium' },
          ] }] },
          { title: 'Relaxant and adjuncts', blocks: [{ type: 'rows', rows: [
            { label: 'Succinylcholine', value: 'First line', note: 'Small rocuronium defasciculating dose if severe myalgias before' },
            { label: 'Rocuronium', value: 'If succinylcholine is out', note: 'Reverse with sugammadex' },
            { label: 'Dexmedetomidine', value: 'Safe', note: 'Blunts the surge, helps a combative wake-up' },
            { label: 'Remifentanil', value: 'Safe', note: 'Helps when seizures are short or weak' },
            { label: 'Caffeine', value: '250–500 mg IV', note: 'Lengthens the seizure' },
            { label: 'Lidocaine', value: 'Avoid', note: 'Shortens the seizure. Small amounts only, and tell the psychiatrist' },
            { label: 'Flumazenil', value: '0.5–1 mg at induction', note: 'Chronic benzodiazepine users. Midazolam before they wake' },
          ] }] },
        ] },
        { id: 'sequence', label: 'Sequence', sections: [
          { title: 'Before the stimulus', blocks: [{ type: 'steps', items: [
            'Monitors on. Mask setup and oxygen ready; LMA and tube within reach. Psychiatry places the EEG',
            'Pre-treat as planned: glycopyrrolate, antihypertensives',
            'Induce, relax, bag-mask ventilate. Hyperventilate a little; it lowers the threshold',
            'Soft bite block between the teeth',
            'Psychiatrist ready, step back',
          ] }] },
          { title: 'The seizure', blocks: [{ type: 'bullets', items: [
            'Stimulus 1–10 s. Wait to ventilate until the EEG says the seizure is over, unless the saturation drops',
            'Over 2 minutes: terminate with midazolam or small propofol doses',
            'Bradycardia or a pause first, then tachycardia and hypertension for minutes to an hour. Short-acting agents: esmolol, nitroglycerin, nicardipine. Labetalol or hydralazine if it persists',
          ] }] },
          { title: 'After', blocks: [{ type: 'bullets', items: [
            'Mask ventilate until awake and breathing',
            'Myalgia and headache: IV acetaminophen and ketorolac',
            'Confusion: redirection, soft restraints, small midazolam or propofol, dexmedetomidine, haloperidol. Call for help',
          ] }] },
        ] },
        { id: 'risk', label: 'Risk', sections: [
          { title: 'Preop focus', blocks: [{ type: 'bullets', items: [
            'Cardiac history and exercise tolerance. Poor tolerance or known CAD: echo or stress test before the course',
            'Stroke, intracranial bleed or surgery, aneurysm. Seizure disorder and antiepileptics',
            'Pacemaker or ICD: interrogation, magnet response, dependence. EP present for the first treatment if in doubt',
          ] }] },
          { title: 'Contraindications', blocks: [{ type: 'bullets', items: [
            { text: 'Absolute: pheochromocytoma', warn: true },
            'Relative: raised ICP or mass, recent stroke, cerebral aneurysm, myocardial ischemia, unstable fractures',
          ] }] },
        ] },
      ],
      sources: [{ title: 'VA anesthesia for ECT', href: GENERAL, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Reaching people', blocks: [{ type: 'bullets', items: [
          'Vocera: pick up any landline, dial 21570, say the name. Spell it if it fails; no "Dr". From a cell, 713-770-1570',
          'Attending not answering: the board runner is your backup. The circulator can call for you',
          'True emergency: ask the circulator to page overhead "Anesthesia staff STAT to OR number"',
          'Staff cell numbers live in QGenda: monthly view, the dots menu, Search Today’s Schedule',
          'Absent or late: call any time and leave a voicemail with your name and callback so the board can change',
        ] }] },
        { title: 'Morning', blocks: [{ type: 'bullets', items: [
          'Check ORC, pull first-case narcotics, set up, holding by 07:00',
          'In the room by 07:30. Wednesdays: Grand Rounds, holding 08:00, room 08:30',
          'Induce and extubate only with the attending present. The board runner covers if you cannot reach them',
          'Time out: the staff surgeon leads for most services. Ask about duration, Foley and antibiotics then',
        ] }] },
        { title: 'End of day', blocks: [{ type: 'bullets', items: [
          'Relieved: see the board runner. Add-on preops, room setups or your own preops may follow',
          'Finish tomorrow’s preops and call the attendings while still in the hospital',
          'The board runner dismisses you',
        ] }] },
        { title: 'Late roles', blocks: [{ type: 'rows', rows: [
          { label: 'DC, day call', value: 'Long case, last out. 07:00 to 07:00 on pager', note: 'Called back past 21:00: tell the board runner when you leave. Ten hours off guaranteed' },
          { label: 'OM, omega', value: 'Second to last out', note: 'Not called back once dismissed' },
        ] }, { type: 'note', text: 'Keep your pager and phone correct in QGenda.' }] },
        { title: 'Resources', blocks: [{ type: 'bullets', items: [
          '"Anesthesia Staff Notes" folder on every OR computer desktop: phone numbers, antibiotic dosing, protocols',
          'Pocket guides from orientation',
          'Badge, CPRS and Innovian codes: the coordinators. OR-area badge access: Facilities Management, 4th floor',
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    orc: {
      title: 'ORC Board',
      sections: [
        { title: 'Reading it', blocks: [{ type: 'bullets', items: [
          'Real time, driven by the clipboard assigned to each patient. Top of the board on All Rooms, All OR Groups',
          'Colours: arrived yellow, SACC or 5H, holding orange, in the OR, closing pink-purple',
          'Each OR box lists the anesthesia staff, CRNA and trainee, the OR nurse’s landline, and event times. The board runner is named',
          'Name and procedure in red. Blood bank notes. Inpatient bed location. Add-ons',
        ] }] },
        { title: 'The three icons', blocks: [{ type: 'rows', rows: [
          { label: 'A', value: 'Anesthesia ready', note: 'You turn it green: black button on the clipboard, or Anesthesia Consult' },
          { label: 'Door', value: 'OR ready', note: 'Red near the deadline: call the OR nurse' },
          { label: 'Stretcher', value: 'Holding paperwork and marking', note: 'Red near the deadline: ask the holding nurse' },
          { label: 'Green flag', value: 'Preop completed', note: 'No Anes PreEval form, then Anes PreEval Form Done' },
        ] }] },
      ],
      sources: [{ title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },

    innovian: {
      title: 'Innovian & CPRS',
      segments: [
        { id: 'preop', label: 'Preop', sections: [
          { title: 'CPRS first', blocks: [{ type: 'bullets', items: [
            'Cover Page, Notes, Labs, Reports. Tools, Vista Imaging for echoes, ECGs and prior anesthesia records titled HOU ARK ANES',
            'Went through the preop clinic: read "Anesthesia Pre-Op Nursing Evaluation Consult", confirm they were cleared, copy labs and meds. The "Hou Ark Pre-op" PDF sits in Vista Imaging',
            'Not seen in clinic but posted in Innovian with an IFN: preop in Innovian anyway. Eye cases, add-ons and inpatients',
            'Not posted: ask a staff member',
          ] }] },
          { title: 'Innovian preop', blocks: [{ type: 'rows', rows: [
            { label: 'Case tab', value: 'ASA, acuity' },
            { label: 'History', value: 'Surgical history from CPRS. MH, PONV. Paste the problem list' },
            { label: 'Present', value: 'Paste the medication list under HPI. Alcohol, tobacco, drugs' },
            { label: 'Systems', value: 'Hysterectomy or LMP under Reproductive. Bleeding tendencies, blood thinners, accepts blood under Other. STOP score under Respiratory if no OSA diagnosis. CXR, ECG, exercise tolerance' },
            { label: 'Exam', value: 'Mallampati required. Breath sounds' },
            { label: 'Plan', value: 'Sign Information Obtained By. Paste vitals' },
            { label: 'Prepare', value: 'Labs under 30 days auto-fill; otherwise paste them' },
          ] }, { type: 'note', text: 'Print from Print Preview. Add the computer room printer HOU-PT143094, or the PACU printer 169538. The sheet carries the SSN; do not lose it.' }] },
        ] },
        { id: 'intraop', label: 'Intraop', sections: [
          { title: 'Open and chart', blocks: [{ type: 'bullets', items: [
            'Search by last name, check the ID and date. Template General, Cardiac or MAC',
            'Events: Patient Arrives in Operating Room, ID Verified with Entire Team for Time Out, induction, intubation, and the rest as they happen',
            'Check vitals are flowing: Intra-Op tab, Chart',
            'Drugs from the Intraop Chart, Intraop Summary or the Drugs icon. Clear every asterisk',
            'Assess: beta blockers and antibiotics with the time out. PIV patency every 60 min and rhythm every 15 min as recurring entries',
            'Methods: the anesthetic, with any spinal or ankle block. Procedures: arterial line, central line, PA catheter. Peripheral IVs under Lines, Tubes, Drains',
            'Required fields are yellow. View, Missing Required Data, often',
          ] }] },
          { title: 'Admission tab', blocks: [{ type: 'bullets', items: [
            'SSN and MRN. ASA, procedure and the surgical IFN from ORC. Anesthesia supervisor, principal anesthetist, surgeon. Last drink and last food under Holding',
          ] }] },
        ] },
        { id: 'close', label: 'Closing', sections: [
          { title: 'Leaving the OR', blocks: [{ type: 'steps', items: [
            'End of Surgery, Emergence, Extubation events, then Patient Leaves the Operating Room',
            'Close with the Close button. Never the X in the corner',
            'Acknowledge every pop-up until the screen is blank gray, or the chart stays locked for the PACU',
          ] }] },
          { title: 'In the PACU', blocks: [{ type: 'steps', items: [
            'Log in with the PACU Phase I environment. A padlock by the name means the OR chart is still open',
            'Temperature by hand; other vitals auto-fill. ICU drop-off: Entry, Manual Vitals Event with the ICU vitals',
            'Events, End of Anesthesia Care, sign the "No Anesthesia Complications" statement',
            'View, Missing Required Data. Narcotic totals must match',
            'Print, New, title HOU ARK ANES. That transmits the record to Vista Imaging and creates the CPRS note to sign',
            'Log out after "Success!" but do not close the chart or stop trending. The PACU nurses need it',
          ] }, { type: 'note', text: 'Student nurse anesthetists cannot print; the attending or CRNA does it.' }] },
        ] },
      ],
      sources: [{ title: 'Innovian charting', href: INNOVIAN, detail: 'Blackboard, sign-in required' }, { title: 'Full VAMC orientation', href: WELCOME, detail: 'Blackboard, sign-in required' }],
    },
  },
};
